import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useScheduleActionLock } from '../../hook/useScheduleActionLock';
import { useLocation } from 'react-router-dom';
import ScheduleTop from '../../components/Schedule/ScheduleTop';
import DailySchedule from '../../components/Schedule/Daily/DailySchedule';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import styled, { keyframes } from 'styled-components';
import dayjs from 'dayjs';
import WeeklySchedule from '../../components/Schedule/Weekly/WeeklySchedule';
import { LEGACY_WEEKLY_ROW_HEIGHT } from '../../components/Schedule/Weekly/layout';
import {
    getInitialLegacyDailyCellHeight,
    clampLegacyDailyCoachPage,
    getLegacyDailyCoachPageCount,
    LEGACY_DAILY_CELL_HEIGHT_MAX,
    LEGACY_DAILY_CELL_HEIGHT_MIN,
    LEGACY_DAILY_CELL_HEIGHT_STEP,
    LEGACY_DAILY_COACH_PAGE_SIZE,
} from '../../components/Schedule/legacyCalendarLayout';
import {
    endOfLegacyMondayWeek,
    isSameLegacyMondayWeek,
    normalizeLegacyMondayWeekScheduleDays,
    startOfLegacyMondayWeek,
} from '../../components/Schedule/legacyCalendarWeek';
import { I_FLOATING, I_FLOATING_HOVER, I_FLOATING_ONCLICK } from '../../types/images';
import ModalCreateClass from '../../components/Schedule/ModalCreateClass';
import GroupSessionCreateModal from '../../components/GroupSession/GroupSessionCreateModal';
import QuickGroupSessionModal from '../../components/GroupSession/QuickGroupSessionModal';
import GroupSessionDetailPopover from '../../components/GroupSession/GroupSessionDetailPopover';
import { useGroupSessions } from '@/features/groupSession/hooks';
import { useNavigate } from 'react-router-dom';
import Calendar from '../../components/Calendar/Calendar';
import { colors } from '../../lib/colors';
import WeeklyCalendar from '../../components/WeeklySchedule/WeeklyCalendar';
import ScheduleDetailModal from '../../components/Schedule/DetailModal/ScheduleDetailModal';
import RequestDetailModal from '../../components/Schedule/DetailModal/RequestDetailModal';
import OffEtcDetailModal from '../../components/Schedule/DetailModal/OffEtcDetailModal';
import NewOpportunityDetailModal from '../../components/Schedule/DetailModal/NewOpportunityDetailModal';
import OrderChangeModal from '../../components/Schedule/Daily/OrderChangeModal';
import ScheduleListView from '../../components/Schedule/ScheduleListView';
import { validateScheduleListRange } from '../../components/Schedule/scheduleListRange';
import ScheduleViewSwitcher from '../../components/Schedule/ScheduleViewSwitcher';
import {
    LegacyCalendarType,
    readLegacyCalendarType,
    readScheduleViewMode,
    readSmartSchedulePeriod,
    ScheduleViewMode,
    ScheduleViewScope,
    scheduleViewScopeKey,
    writeLegacyCalendarType,
    writeScheduleViewMode,
    writeSmartSchedulePeriod,
} from '../../components/Schedule/scheduleViewPreferences';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    approveScheduleRequestAPI,
    getDailyScheduleAPI,
    getRangeScheduleAPI,
    getWeeklyScheduleAPI,
    rejectScheduleRequestAPI,
} from '../../api/schedule';
import { getSharedScheduleErrorMessage } from '../../features/sharedMembership/errors';
import { getNewOpportunityCalendarAPI } from '../../api/newOpportunity';
import { getCoachNameListAPI, patchCoachOrder } from '../../api/center';
import BusinessInfoFooter from '../../components/BusinessInfoFooter';
import { useCenterFeatureFlags } from '../../hooks/useCenterFeatureFlags';
import { resolveHistoryMemberId } from '../../lib/scheduleHistory';
import { QuickMoveProvider } from '../../components/Schedule/QuickMove/QuickMoveProvider';
import SmartScheduleView from '../../components/Schedule/Smart/SmartScheduleView';
import {
    SmartScheduleEvent,
    SmartSchedulePeriod,
    countVisibleSmartScheduleEvents,
    countVisibleSmartScheduleEventsByCoach,
    getVisibleSmartScheduleCountBreakdown,
    getSmartScheduleRange,
    normalizeSmartScheduleData,
    resolveSmartScheduleGroupSessionId,
    toSmartScheduleKst,
} from '../../features/smartSchedule/model';

const normalizeNewOpportunitySchedule = (opportunity: any) => {
    const startTime = opportunity.startTime;
    const endTime = opportunity.endTime;
    const displayName =
        opportunity.displayName ||
        opportunity.categoryName ||
        opportunity.name ||
        '수업';
    const memberName = opportunity.memberName || '';
    return {
        ...opportunity,
        _id: opportunity._id || opportunity.id || opportunity.newOpportunityId,
        type: 'newOpportunity',
        eventType: 'newOpportunity',
        displayName,
        title: `신규기회 · ${displayName}`,
        name: `신규기회 · ${displayName}`,
        alias: memberName,
        coachId: opportunity.coachId,
        coachName: opportunity.coachName || '',
        startTime,
        endTime,
        duration: opportunity.duration || dayjs(endTime).diff(dayjs(startTime), 'minute'),
        newOpportunityId: opportunity.newOpportunityId || opportunity._id || opportunity.id,
    };
};

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const DEFAULT_SCHEDULE_START_HOUR = 6;
const SCHEDULE_FIXED_AREA_HEIGHT = 360;
const WEEKLY_CELL_HEIGHT = LEGACY_WEEKLY_ROW_HEIGHT;

const selectCoachNameList = (response: any) =>
    (response?.data?.coachNameList || []).map((coach: any) => ({
        code: coach.coachId,
        value: coach.name,
        object: coach,
    }));

const sortCoachRowsByIds = (coaches: any[], coachIds: string[]) => {
    const orderById = new Map(coachIds.map((coachId, index) => [String(coachId), index]));
    return [...(coaches || [])].sort((left, right) => {
        const leftOrder = orderById.get(String(left?.coachId));
        const rightOrder = orderById.get(String(right?.coachId));
        return (
            (leftOrder ?? Number.MAX_SAFE_INTEGER) - (rightOrder ?? Number.MAX_SAFE_INTEGER)
        );
    });
};


const getDefaultListRange = (calendarType: 'daily' | 'weekly', selectedDay: any) => {
    const selectedDate = dayjs(selectedDay);
    if (calendarType === 'weekly') {
        return {
            startDate: startOfLegacyMondayWeek(selectedDate).format('YYYY-MM-DD'),
            endDate: endOfLegacyMondayWeek(selectedDate).format('YYYY-MM-DD'),
        };
    }

    return {
        startDate: selectedDate.format('YYYY-MM-DD'),
        endDate: selectedDate.format('YYYY-MM-DD'),
    };
};

const getCalendarScrollYForHour = (
    calendarType: 'daily' | 'weekly',
    hour: number,
    dailyCellHeight: number
) => {
    const calendarCellHeight = calendarType === 'daily' ? dailyCellHeight : WEEKLY_CELL_HEIGHT;
    return Math.max(0, hour < 2 ? calendarCellHeight * hour : calendarCellHeight * (hour - 2));
};

const getCalendarScrollYForTime = (
    calendarType: 'daily' | 'weekly',
    time: dayjs.Dayjs,
    dailyCellHeight: number
) => {
    const calendarCellHeight = calendarType === 'daily' ? dailyCellHeight : WEEKLY_CELL_HEIGHT;
    return getCalendarScrollYForHour(calendarType, time.hour(), dailyCellHeight) +
        (time.minute() / 60) * calendarCellHeight;
};

const getDefaultScheduleScrollY = (
    calendarType: 'daily' | 'weekly',
    selectedDay: any,
    dailyCellHeight: number
) => {
    const selectedDate = dayjs(selectedDay);
    const now = dayjs();
    const shouldCenterCurrentTime =
        (calendarType === 'daily' && selectedDate.isSame(now, 'day')) ||
        (calendarType === 'weekly' && isSameLegacyMondayWeek(selectedDate, now));

    if (shouldCenterCurrentTime) {
        const visibleScheduleHeight = Math.max(dailyCellHeight * 3, window.innerHeight - SCHEDULE_FIXED_AREA_HEIGHT);
        return Math.max(0, getCalendarScrollYForTime(calendarType, now, dailyCellHeight) - visibleScheduleHeight / 2);
    }

    return getCalendarScrollYForHour(calendarType, DEFAULT_SCHEDULE_START_HOUR, dailyCellHeight);
};

const getScheduleScrollContainer = () => {
    const app = document.querySelector<HTMLElement>('#App');
    if (app) {
        return app;
    }
    return document.scrollingElement as HTMLElement | null;
};

const getScheduleScrollTop = () => {
    const scrollContainer = getScheduleScrollContainer();
    return scrollContainer?.scrollTop ?? window.scrollY;
};

const scrollSchedulePageTo = (top: number) => {
    const scrollContainer = getScheduleScrollContainer();
    scrollContainer?.scrollTo({ top, behavior: 'auto' });
    window.scrollTo({ top, behavior: 'auto' });
};

const ColContainer = styled.div`
    display: flex;
    flex-direction: column;
    animation: ${fadeIn} 1s ease; /* 애니메이션 적용 */
    transition: width 0.5s ease;
`;

const ScheduleViewDock = styled.div`
    position: fixed;
    z-index: 45;
    top: 95px;
    left: 50%;
    display: flex;
    align-items: center;
    width: max-content;
    max-width: calc(100vw - 24px);
    height: 36px;
    transform: translateX(-50%);
`;

const SchedulePage = ({ setIsLoading, createAlert, createTwoButtonAlert }: any) => {
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));
    const [authObject, setAuthObject] = useState(getItemWithExpiration('authObject'));
    const { isNewOpportunityEnabled } = useCenterFeatureFlags();

    const navigate = useNavigate();
    const location = useLocation();

    // 캘린더 페이지의 일간 주간 타입 정보
    const calendarType = location.state?.calendarType;

    // 일정 등록 / 수정 시 받아올 시간
    const calendarTimeInfo = location.state?.calendarTimeInfo;

    // 일정 페이지로 갔다가 되돌아올 때 받아올 날짜 및 스크롤 정보
    const calendarSelectedDay = location.state?.calendarSelectedDay;
    const calendarScrollY = location.state?.calendarScrollY;
    const calendarCoachId = location.state?.calendarCoachId;
    const hasCalendarScrollY = Object.prototype.hasOwnProperty.call(location.state ?? {}, 'calendarScrollY');
    const shouldRestoreCalendarScroll = hasCalendarScrollY && Number(calendarScrollY) > 0;

    const [currentDate, setCurrentDate] = useState(dayjs());
    // 보기 설정은 센터 + 관리자 계정 단위로 저장한다. 한 PC를 여러 직원이 공유해도
    // 마지막으로 본 화면이 서로 덮어써지지 않게 하기 위함.
    const scheduleViewScope: ScheduleViewScope = useMemo(
        () => ({ centerId: authObject?.centerId, accountId: authObject?.adminId || authObject?.id }),
        [authObject?.adminId, authObject?.centerId, authObject?.id]
    );
    const [pageType, setPageType] = useState<LegacyCalendarType>(() => readLegacyCalendarType(scheduleViewScope));
    const [viewMode, setViewMode] = useState<ScheduleViewMode>(() => readScheduleViewMode(scheduleViewScope));
    const [smartPeriod, setSmartPeriod] = useState<SmartSchedulePeriod>(() =>
        readSmartSchedulePeriod(scheduleViewScope)
    );
    const scheduleSettingsCenterKey = scheduleViewScopeKey(scheduleViewScope);
    const [hydratedScheduleSettingsCenterKey, setHydratedScheduleSettingsCenterKey] = useState(
        scheduleSettingsCenterKey
    );
    const handleViewModeChange = useCallback(
        (nextViewMode: ScheduleViewMode) => {
            writeScheduleViewMode(scheduleViewScope, nextViewMode);
            setViewMode(nextViewMode);
        },
        [scheduleViewScope]
    );
    const [selectedDay, setSelectedDay] = useState<any>(new Date());
    const [selectedSchedule, setSelectedSchedule] = useState<any>(null);
    const initialListRange = getDefaultListRange('daily', new Date());
    const [listRangeStartDate, setListRangeStartDate] = useState(initialListRange.startDate);
    const [listRangeEndDate, setListRangeEndDate] = useState(initialListRange.endDate);
    const lastAutoScrollSignatureRef = useRef<string | null>(null);
    const hasInitializedListRangeRef = useRef(false);

    // 플로팅 버튼 누르면 나오는 모달 boolean
    const [isCreateModal, setIsCreateModal] = useState<boolean>(false);
    const [isGroupSessionCreateModal, setIsGroupSessionCreateModal] = useState(false); // 새 수업 템플릿 만들기 (전체 폼)
    /*
     * 스케줄 동작 잠금(설계 rappo docs/SCHEDULE_ACTION_LOCK_DESIGN.md §6).
     * 생성: 새 일정 선택창 열기(하단 +, 목록 「일정 만들기」, 빈칸 클릭 두 종류)·그룹 전체 폼 열기.
     * 수정: 신청 승인·거절(종류 무관, 화면 4곳이 아래 두 함수로 모인다).
     */
    const createLock = useScheduleActionLock('schedule.create');
    const editLock = useScheduleActionLock('schedule.edit');
    const [isQuickGroupModal, setIsQuickGroupModal] = useState(false); // 빠른 세션 생성 (템플릿 선택)
    const [quickGroupDefaults, setQuickGroupDefaults] = useState<{ date?: string; startTime?: string; endTime?: string; coachId?: string; coachName?: string }>({});
    const [groupSessionDetailId, setGroupSessionDetailId] = useState<string | null>(null);
    const [isCalendar, setIsCalendar] = useState<boolean>(false);

    // 수업일정 자세히보기 모달
    const [detailObject, setDetailObject] = useState<any>(null);
    const [isScheduleDetailModal, setIsScheduleDetailModal] = useState<boolean>(false);

    const [isRequestDetailModal, setIsRequestDetailModal] = useState<boolean>(false);
    const [isNewOpportunityDetailModal, setIsNewOpportunityDetailModal] = useState<boolean>(false);

    // OFF ,  일반일정 자세히보기 모달
    const [isOffEtcDetailModal, setIsOffEtcDetailModal] = useState<boolean>(false);
    const [scrollY, setScrollY] = useState<number>(0);

    // 순서 변경 모달
    const [isOrderChangeModal, setIsOrderChangeModal] = useState<boolean>(false);

    // 줌 레벨 (셀 높이)
    const [cellHeight, setCellHeight] = useState<number>(() => {
        return getInitialLegacyDailyCellHeight(sessionStorage.getItem('scheduleCellHeight'));
    });

    const handleZoomIn = useCallback(() => {
        setCellHeight((prev) => {
            const next = Math.min(prev + LEGACY_DAILY_CELL_HEIGHT_STEP, LEGACY_DAILY_CELL_HEIGHT_MAX);
            sessionStorage.setItem('scheduleCellHeight', String(next));
            return next;
        });
    }, []);

    const handleZoomOut = useCallback(() => {
        setCellHeight((prev) => {
            const next = Math.max(prev - LEGACY_DAILY_CELL_HEIGHT_STEP, LEGACY_DAILY_CELL_HEIGHT_MIN);
            sessionStorage.setItem('scheduleCellHeight', String(next));
            return next;
        });
    }, []);

    // 기존 캘린더의 전역 로딩 오버레이는 아래 legacy 조건만 사용한다.
    // 한눈에 일간에서도 같은 응답을 강사 순서 원본으로 읽지만, 그 로딩은 한눈에 내부에서 처리한다.
    const isLegacyDailyQueryEnabled =
        viewMode === 'calendar' && pageType === 'daily' && !!authorization && !!!authObject?.testAccount;
    // 한눈에 일간의 강사 행은 기존 캘린더와 같은 center.relations 순서를 따라야 한다.
    // coach-name-list는 별도의 CoachCenter.sortOrder를 사용하므로 순서 변경 API 결과가 반영되지 않는다.
    const isSmartCoachOrderQueryEnabled =
        viewMode === 'smart' &&
        smartPeriod === 'day' &&
        !!authObject?.centerId &&
        !!authorization &&
        !!!authObject?.testAccount;
    const isDailyScheduleQueryEnabled = isLegacyDailyQueryEnabled || isSmartCoachOrderQueryEnabled;
    const isLegacyWeeklyQueryEnabled =
        viewMode === 'calendar' && pageType === 'weekly' && !!authorization && !!!authObject?.testAccount;

    /***** 일간일정 속성 */

    /** 초기값 설정 */
    const [dailyScheduleList, setDailyScheduleList] = useState<any>(null);
    const [gridData, setGridData] = useState<any>(null);

    /** 페이징 처리 */
    const pageSize = LEGACY_DAILY_COACH_PAGE_SIZE;
    const [currentPage, setCurrentPage] = useState<number>(1);
    const focusedCoachPageSignatureRef = useRef<string | null>(null);
    const previousDailyCenterIdRef = useRef<string | undefined>(authObject?.centerId);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const [dailyCurrentData, setDailyCurrentData] = useState<any>(null);
    const [totalCoachData, setTotalCoachData] = useState<any>(null);

    useEffect(() => {
        if (previousDailyCenterIdRef.current === authObject?.centerId) return;
        previousDailyCenterIdRef.current = authObject?.centerId;
        focusedCoachPageSignatureRef.current = null;
        setCurrentPage(1);
        setDailyScheduleList(null);
        setDailyCurrentData(null);
        setTotalCoachData(null);
        setGridData(null);
    }, [authObject?.centerId]);

    // 일간 강사 스케줄 조회 API : (GET)
    const getDailySchedule = useQuery(
        ['getDailyScheduleAPI', authObject?.centerId, dayjs(selectedDay).format('YYYY-MM-DD')],
        async () =>
            await getDailyScheduleAPI(authObject.centerId, dayjs(selectedDay).format('YYYY-MM-DD'), authorization),
        {
            onSuccess: (res) => {
                if (res.status === 200) {
                    setDailyScheduleList(res.data.dailySchedules);
                    // totalCoachData는 mergedDailyData에서 설정됨
                }
            },
            onError: (err) => {
                console.log(err);
            },
            enabled: isDailyScheduleQueryEnabled,
            // refetchOnWindowFocus: 'always',
            staleTime: 0,
        }
    );

    // 그룹 세션 조회 + daily 데이터에 병합
    const selectedDayStr = dayjs(selectedDay).format('YYYY-MM-DD');
    const { data: groupSessions = [], isError: isLegacyDailyGroupError } = useGroupSessions({
        centerId: authObject?.centerId,
        startDate: selectedDayStr,
        endDate: selectedDayStr,
        enabled: viewMode === 'calendar' && pageType === 'daily' && !!authObject?.centerId,
    });

    const {
        data: dailyNewOpportunityResponse,
        isError: isDailyNewOpportunityError,
        isFetching: isDailyNewOpportunityFetching,
        refetch: refetchDailyNewOpportunity,
    } = useQuery(
        ['newOpportunityCalendar', 'daily', authObject?.centerId, selectedDayStr],
        async () =>
            await getNewOpportunityCalendarAPI(authObject.centerId, authorization, {
                startTime: dayjs(selectedDay).startOf('day').toISOString(),
                endTime: dayjs(selectedDay).add(1, 'day').startOf('day').toISOString(),
            }),
        {
            enabled:
                viewMode === 'calendar' &&
                pageType === 'daily' &&
                isNewOpportunityEnabled &&
                !!authorization &&
                !!authObject?.centerId &&
                !!!authObject?.testAccount,
            staleTime: 0,
        }
    );
    const dailyNewOpportunities = useMemo(
        () => dailyNewOpportunityResponse?.data?.results || [],
        [dailyNewOpportunityResponse]
    );

    // 그룹 세션과 신규기회를 기존 dailyScheduleList 구조에 병합
    const mergedDailyData = useMemo(() => {
        if (!dailyScheduleList) return dailyScheduleList;
        if (groupSessions.length === 0 && dailyNewOpportunities.length === 0) return dailyScheduleList;
        const merged = dailyScheduleList.map((coach: any) => ({
            ...coach,
            schedules: [...(coach.schedules || [])],
        }));

        const addScheduleItem = (scheduleItem: any) => {
            if (!scheduleItem.coachId) return;
            const coachIdx = merged.findIndex((c: any) => c.coachId === scheduleItem.coachId);
            if (coachIdx >= 0) {
                merged[coachIdx].schedules.push(scheduleItem);
                return;
            }
            merged.push({
                coachId: scheduleItem.coachId,
                coachName: scheduleItem.coachName || scheduleItem.title,
                coachProfileImageUrl: scheduleItem.coachProfileImageUrl || '',
                schedules: [scheduleItem],
            });
        };

        for (const gs of groupSessions) {
            if (!gs.coachId) continue;
            const scheduleItem = {
                _id: gs._id,
                type: 'schedule',
                classType: gs.classType,
                title: gs.title,
                alias: gs.title,
                coachId: gs.coachId,
                coachName: gs.coachName || '',
                startTime: gs.startTime,
                endTime: gs.endTime,
                duration: gs.duration,
                currentCount: gs.currentCount,
                maxCapacity: gs.maxCapacity,
                status: gs.status,
            };
            addScheduleItem(scheduleItem);
        }

        for (const opportunity of dailyNewOpportunities) {
            addScheduleItem(normalizeNewOpportunitySchedule(opportunity));
        }
        return merged;
    }, [dailyScheduleList, groupSessions, dailyNewOpportunities]);

    const dailyDataSource = mergedDailyData || dailyScheduleList;
    const legacyDailyScheduleEvents = useMemo(
        () =>
            normalizeSmartScheduleData({
                rangeSchedules: dailyScheduleList || [],
                groupSessions,
                newOpportunities: dailyNewOpportunities,
            }).events,
        [dailyNewOpportunities, dailyScheduleList, groupSessions]
    );
    const legacyDailyScheduleCount = useMemo(
        () => countVisibleSmartScheduleEvents(legacyDailyScheduleEvents),
        [legacyDailyScheduleEvents]
    );
    const legacyDailyScheduleCountBreakdown = useMemo(
        () => getVisibleSmartScheduleCountBreakdown(legacyDailyScheduleEvents),
        [legacyDailyScheduleEvents]
    );
    const legacyDailyScheduleCountByCoach = useMemo(
        () => countVisibleSmartScheduleEventsByCoach(legacyDailyScheduleEvents),
        [legacyDailyScheduleEvents]
    );
    const endPage = dailyDataSource ? getLegacyDailyCoachPageCount(dailyDataSource.length) : 0;

    const handleNextPage = () => {
        setCurrentPage((page) => Math.min(page + 1, endPage));
    };

    const handlePrevPage = () => {
        setCurrentPage((page) => Math.max(page - 1, 1));
    };

    // 호버클릭시 : 하단플로팅 버튼과 동일한 모달
    const onClickHover = (scheduleTime: any, coachName: string, coachId: string) => {
        onClickFloatingButton();
        setSelectedSchedule({
            scheduleStartTime: dayjs(scheduleTime).format('YYYY-MM-DD HH:mm'),
            coachName: coachName,
            coachId: coachId,
        });
    };

    /***** 일간일정 속성 끝 */

    /***** 주간일정 속성 */
    const [weeklyGridData, setWeeklyGridData] = useState<any>(null); // 그리드 그리는 데이터
    const [weeklyScheduleList, setWeeklyScheduleList] = useState<any>(null);
    const [selectedCoach, setselectedCoach] = useState<any>({
        code: 'all',
        name: '모든 강사',
    });
    const selectedCoachIdForCalendar =
        selectedCoach?.code && selectedCoach.code !== 'all' ? selectedCoach.code : undefined;

    // 주간용 그룹 세션 조회
    const weekStart = startOfLegacyMondayWeek(selectedDay);
    const weekEnd = endOfLegacyMondayWeek(selectedDay);
    const apiSundayWeekStartStr = weekStart.subtract(1, 'day').format('YYYY-MM-DD');
    const weekStartStr = weekStart.format('YYYY-MM-DD');
    const weekEndStr = weekEnd.format('YYYY-MM-DD');
    const { data: weeklyGroupSessions = [], isError: isLegacyWeeklyGroupError } = useGroupSessions({
        centerId: authObject?.centerId,
        startDate: weekStartStr,
        endDate: weekEndStr,
        coachId: selectedCoachIdForCalendar,
        enabled: viewMode === 'calendar' && pageType === 'weekly' && !!authObject?.centerId,
    });

    const [lengthArr, setLengthArr] = useState<any>(); // 각시간마다의 길이만 담고있는 배열

    const [currentHeight, setCurrentHeight] = useState<number>(0);
    const [totalHeight, setTotalHeight] = useState<number>(0);

    const listRangeValidation = useMemo(
        () => validateScheduleListRange(listRangeStartDate, listRangeEndDate),
        [listRangeEndDate, listRangeStartDate]
    );
    const selectedCoachIdForList = selectedCoach?.code && selectedCoach.code !== 'all' ? selectedCoach.code : '';

    const listRangeScheduleQuery = useQuery(
        [
            'getListRangeSchedulesAPI',
            authObject?.centerId,
            listRangeStartDate,
            listRangeEndDate,
            selectedCoachIdForList,
        ],
        async () => {
            const res = await getRangeScheduleAPI(
                authObject.centerId,
                listRangeStartDate,
                listRangeEndDate,
                selectedCoachIdForList,
                authorization
            );
            if (res?.status !== 200) {
                throw new Error(res?.data?.message || '스케줄 목록을 불러오지 못했습니다.');
            }
            return res?.data?.schedules || [];
        },
        {
            enabled:
                viewMode === 'list' &&
                !!authorization &&
                !!authObject?.centerId &&
                !!!authObject?.testAccount &&
                listRangeValidation.isValid,
            staleTime: 60 * 1000,
        }
    );

    const {
        data: listRangeGroupSessions = [],
        isFetching: isListRangeGroupSessionsFetching,
        isError: isListRangeGroupSessionsError,
        refetch: refetchListRangeGroupSessions,
    } = useGroupSessions({
        centerId: authObject?.centerId,
        startDate: listRangeStartDate,
        endDate: listRangeEndDate,
        enabled:
            viewMode === 'list' &&
            !!authObject?.centerId &&
            listRangeValidation.isValid,
    });

    const {
        data: listRangeNewOpportunityResponse,
        isFetching: isListRangeNewOpportunityFetching,
        isError: isListRangeNewOpportunityError,
        refetch: refetchListRangeNewOpportunity,
    } = useQuery(
        ['newOpportunityCalendar', 'list', authObject?.centerId, listRangeStartDate, listRangeEndDate, selectedCoach?.code],
        async () =>
            await getNewOpportunityCalendarAPI(authObject.centerId, authorization, {
                startTime: dayjs(listRangeStartDate).startOf('day').toISOString(),
                endTime: dayjs(listRangeEndDate).endOf('day').toISOString(),
                coachId: selectedCoachIdForList || undefined,
            }),
        {
            enabled:
                viewMode === 'list' &&
                isNewOpportunityEnabled &&
                !!authorization &&
                !!authObject?.centerId &&
                !!!authObject?.testAccount &&
                listRangeValidation.isValid,
            staleTime: 0,
        }
    );

    const listRangeScheduleItems = useMemo(() => {
        const scheduleItems = (listRangeScheduleQuery.data || []).filter(
            (schedule: any) => !selectedCoachIdForList || schedule.coachId === selectedCoachIdForList
        );

        for (const gs of listRangeGroupSessions) {
            if (selectedCoachIdForList && gs.coachId !== selectedCoachIdForList) continue;
            scheduleItems.push({
                _id: gs._id,
                type: 'schedule',
                classType: gs.classType,
                title: gs.title,
                alias: gs.title,
                coachId: gs.coachId || '',
                coachName: gs.coachName || '',
                startTime: gs.startTime,
                endTime: gs.endTime,
                duration: gs.duration,
                currentCount: gs.currentCount,
                maxCapacity: gs.maxCapacity,
                status: gs.status,
            });
        }

        for (const opportunity of listRangeNewOpportunityResponse?.data?.results || []) {
            scheduleItems.push(normalizeNewOpportunitySchedule(opportunity));
        }

        return scheduleItems;
    }, [listRangeGroupSessions, listRangeNewOpportunityResponse, listRangeScheduleQuery.data, selectedCoachIdForList]);

    const isListRangeLoading =
        listRangeScheduleQuery.isFetching ||
        isListRangeGroupSessionsFetching ||
        isListRangeNewOpportunityFetching;
    const hasListRangeQueryError =
        listRangeScheduleQuery.isError || isListRangeGroupSessionsError || isListRangeNewOpportunityError;
    const listRangeErrorMessage = !listRangeValidation.isValid
        ? listRangeValidation.errorMessage
        : hasListRangeQueryError
        ? '일정을 불러오지 못했습니다. 잠시 후 기간 적용을 다시 눌러주세요.'
        : '';

    const {
        data: weeklyNewOpportunityResponse,
        isError: isWeeklyNewOpportunityError,
        isFetching: isWeeklyNewOpportunityFetching,
        refetch: refetchWeeklyNewOpportunity,
    } = useQuery(
        ['newOpportunityCalendar', 'weekly', authObject?.centerId, weekStartStr, weekEndStr, selectedCoach?.code],
        async () =>
            await getNewOpportunityCalendarAPI(authObject.centerId, authorization, {
                startTime: weekStart.toISOString(),
                endTime: weekEnd.add(1, 'millisecond').toISOString(),
                coachId: selectedCoach?.code && selectedCoach.code !== 'all' ? selectedCoach.code : undefined,
            }),
        {
            enabled:
                viewMode === 'calendar' &&
                pageType === 'weekly' &&
                isNewOpportunityEnabled &&
                !!authorization &&
                !!authObject?.centerId &&
                !!!authObject?.testAccount,
            staleTime: 0,
        }
    );
    const weeklyNewOpportunities = useMemo(
        () => weeklyNewOpportunityResponse?.data?.results || [],
        [weeklyNewOpportunityResponse]
    );

    // API : (GET) 주간 강사 스케줄 조회
    const getWeeklySchedule = useQuery(
        ['getWeeklyScheduleAPI', authObject?.centerId, weekStartStr, selectedCoach?.code],
        async () => {
            const [currentSundayWeekResponse, followingSundayWeekResponse] = await Promise.all([
                getWeeklyScheduleAPI(
                    authObject.centerId,
                    apiSundayWeekStartStr,
                    selectedCoach?.code ?? '',
                    authorization
                ),
                getWeeklyScheduleAPI(
                    authObject.centerId,
                    weekEndStr,
                    selectedCoach?.code ?? '',
                    authorization
                ),
            ]);

            const currentSundayWeekSchedules = Array.isArray(currentSundayWeekResponse?.data?.weeklySchedules)
                ? currentSundayWeekResponse.data.weeklySchedules
                : [];
            if (currentSundayWeekResponse?.status !== 200) {
                return currentSundayWeekResponse;
            }
            const followingSundayWeekSchedules = Array.isArray(followingSundayWeekResponse?.data?.weeklySchedules)
                ? followingSundayWeekResponse.data.weeklySchedules
                : [];
            const normalizedSchedules = normalizeLegacyMondayWeekScheduleDays(
                currentSundayWeekSchedules,
                selectedDay,
                followingSundayWeekSchedules
            );

            return {
                ...currentSundayWeekResponse,
                data: {
                    ...currentSundayWeekResponse.data,
                    weeklySchedules: normalizedSchedules,
                },
            };
        },
        {
            onSuccess: (res) => {
                if (res.status === 200) {
                    const apiData = Array.isArray(res.data.weeklySchedules) ? res.data.weeklySchedules : [];
                    setWeeklyScheduleList(apiData);

                    if (apiData) {
                        let weeklyGridData: any = Array.from({ length: 24 }, (_, rowIndex) =>
                            Array.from({ length: 8 }, (_, colIndex) =>
                                colIndex === 0
                                    ? {
                                          id: `${rowIndex}-${colIndex}`,
                                          content:
                                              rowIndex === 0
                                                  ? `자정 12시`
                                                  : rowIndex < 12
                                                  ? `오전 ${rowIndex.toString().padStart(2, '0')}시`
                                                  : rowIndex === 12
                                                  ? `정오 ${rowIndex}시`
                                                  : `오후 ${(rowIndex - 12).toString().padStart(2, '0')}시`,
                                      }
                                    : []
                            )
                        );

                        // weeklyGridData[세로열 (시간 0~23)][가로열 (날짜 id열 + 7일)]

                        for (let i = 0; i < apiData.length; i++) {
                            const daySchedules = Array.isArray(apiData[i]?.schedules) ? apiData[i].schedules : [];
                            for (let j = 0; j < 24; j++) {
                                if (weeklyGridData[j] && i + 1 < weeklyGridData[j].length) {
                                    weeklyGridData[j][i + 1] = Array.isArray(daySchedules[j]) ? daySchedules[j] : [];
                                }
                            }
                        }
                        setWeeklyGridData(weeklyGridData);

                        // 시간 배열생성
                        let arr = Array.from({ length: 24 }, () => new Array(7).fill(0));
                        for (let i = 0; i < apiData.length; i++) {
                            const daySchedules = Array.isArray(apiData[i]?.schedules) ? apiData[i].schedules : [];
                            for (let j = 0; j < 24; j++) {
                                const schedules = Array.isArray(daySchedules[j]) ? daySchedules[j] : [];
                                if (schedules.length === 0) continue;
                                let scheduleCount = 0;
                                let lastCoachId = '';
                                let lastStartTime = '';
                                for (const schedule of schedules) {
                                    if (schedule.coachId == lastCoachId && schedule.startTime == lastStartTime) {
                                        continue;
                                    }
                                    lastCoachId = schedule.coachId;
                                    lastStartTime = schedule.startTime;
                                    scheduleCount++;
                                }
                                arr[j][i] = scheduleCount;
                            }
                        }

                        setLengthArr(arr);

                        // 블러처리를 위한 높이구하기
                        let totalHeightArr = [];
                        let currentHeightArr = [];
                        let currentHour = parseInt(dayjs().format('H'));

                        for (let i = 0; i < arr.length; i++) {
                            totalHeightArr.push(Math.max(...arr[i]));
                        }

                        for (let i = 0; i < currentHour; i++) {
                            currentHeightArr.push(Math.max(...arr[i]));
                        }

                        const totalSum = totalHeightArr.reduce(
                            (acc: number, number: number) =>
                                acc + Math.max(1, number) * LEGACY_WEEKLY_ROW_HEIGHT,
                            0
                        );

                        const currentSum = currentHeightArr.reduce(
                            (acc: number, number: number) =>
                                acc + Math.max(1, number) * LEGACY_WEEKLY_ROW_HEIGHT,
                            0
                        );
                        // 현재시간 이 포함된 전에 스케줄박스까지의 높이  + 현재 시간에 걸쳐진 박스에 높이

                        if (isSameLegacyMondayWeek(selectedDay, dayjs())) {
                            setCurrentHeight(currentSum);
                            setTotalHeight(totalSum);
                        } else {
                            setCurrentHeight(0);
                            setTotalHeight(0);
                        }
                    }
                }
            },
            onError: (err) => {
                console.log(err);
            },
            enabled: isLegacyWeeklyQueryEnabled,
            // refetchOnWindowFocus: 'always',
            staleTime: 0,
        }
    );

    // 그룹 세션과 신규기회를 주간 그리드에 병합 (useMemo로 파생 — onSuccess 콜백이 덮어쓰지 않음)
    const mergedWeeklyGridData = useMemo(() => {
        if (!weeklyGridData) return weeklyGridData;
        if (weeklyGroupSessions.length === 0 && weeklyNewOpportunities.length === 0) return weeklyGridData;
        const weekStart = startOfLegacyMondayWeek(selectedDay);
        const merged = weeklyGridData.map((row: any) => row.map((cell: any) =>
            Array.isArray(cell) ? [...cell] : cell
        ));

        const addWeeklyScheduleItem = (scheduleItem: any) => {
            const sessionStart = dayjs(scheduleItem.startTime);
            const dayIdx = sessionStart.diff(weekStart, 'day');
            const hour = sessionStart.hour();
            if (dayIdx < 0 || dayIdx > 6 || hour < 0 || hour > 23) return;
            const colIdx = dayIdx + 1;

            const existing = merged[hour]?.[colIdx];
            if (Array.isArray(existing)) {
                if (existing.some((s: any) => s._id === scheduleItem._id && s.type === scheduleItem.type)) return;
                existing.push(scheduleItem);
            } else {
                merged[hour][colIdx] = [scheduleItem];
            }
        };

        for (const gs of weeklyGroupSessions) {
            const scheduleItem = {
                _id: gs._id,
                type: 'schedule',
                classType: gs.classType,
                title: gs.title,
                alias: gs.title,
                coachId: gs.coachId || '',
                coachName: gs.coachName || '',
                startTime: gs.startTime,
                endTime: gs.endTime,
                duration: gs.duration,
                currentCount: gs.currentCount,
                maxCapacity: gs.maxCapacity,
                status: gs.status,
            };
            addWeeklyScheduleItem(scheduleItem);
        }

        for (const opportunity of weeklyNewOpportunities) {
            addWeeklyScheduleItem(normalizeNewOpportunitySchedule(opportunity));
        }
        return merged;
    }, [weeklyGridData, weeklyGroupSessions, weeklyNewOpportunities, selectedDay]);

    // 그룹 세션 포함된 lengthArr 파생
    const mergedLengthArr = useMemo(() => {
        if (!lengthArr || !mergedWeeklyGridData) return lengthArr;
        // mergedWeeklyGridData 기반으로 각 셀의 스케줄 수를 다시 계산
        const arr = Array.from({ length: 24 }, () => new Array(7).fill(0));
        for (let hour = 0; hour < 24; hour++) {
            for (let day = 0; day < 7; day++) {
                const cell = mergedWeeklyGridData[hour]?.[day + 1];
                if (!Array.isArray(cell) || cell.length === 0) {
                    arr[hour][day] = lengthArr[hour]?.[day] ?? 0;
                    continue;
                }
                // 기존 로직과 동일: coachId+startTime 기준 중복 제거하여 카운트
                let count = 0;
                let lastCoachId = '';
                let lastStartTime = '';
                for (const s of cell) {
                    if (s.coachId === lastCoachId && s.startTime === lastStartTime) continue;
                    lastCoachId = s.coachId;
                    lastStartTime = s.startTime;
                    count++;
                }
                arr[hour][day] = Math.max(count, lengthArr[hour]?.[day] ?? 0);
            }
        }
        return arr;
    }, [lengthArr, mergedWeeklyGridData]);

    const legacyWeeklyScheduleEvents = useMemo(
        () =>
            normalizeSmartScheduleData({
                rangeSchedules: weeklyScheduleList || [],
                groupSessions: weeklyGroupSessions,
                newOpportunities: weeklyNewOpportunities,
            }).events,
        [weeklyGroupSessions, weeklyNewOpportunities, weeklyScheduleList]
    );
    const legacyWeeklyScheduleCount = useMemo(
        () => countVisibleSmartScheduleEvents(legacyWeeklyScheduleEvents),
        [legacyWeeklyScheduleEvents]
    );
    const legacyWeeklyScheduleCountBreakdown = useMemo(
        () => getVisibleSmartScheduleCountBreakdown(legacyWeeklyScheduleEvents),
        [legacyWeeklyScheduleEvents]
    );
    const legacyWeeklyScheduleCountByCoach = useMemo(
        () => countVisibleSmartScheduleEventsByCoach(legacyWeeklyScheduleEvents),
        [legacyWeeklyScheduleEvents]
    );

    // 로딩 플래그 — isInitialLoading은 disabled 쿼리에서 false이므로 오버레이가 헛도는 일이 없고,
    // cleanup으로 언마운트·조건 전환 시 전역 오버레이가 잔류하지 않게 반드시 해제한다
    useEffect(() => {
        if (!isLegacyWeeklyQueryEnabled) return;
        setIsLoading(getWeeklySchedule.isInitialLoading);
        return () => setIsLoading(false);
    }, [getWeeklySchedule.isInitialLoading, isLegacyWeeklyQueryEnabled, setIsLoading]);
    /***** 주간일정 속성 끝 */

    // 플로팅 버튼 클릭이벤트
    const onClickFloatingButton = () => {
        createLock.run(() => setIsCreateModal(true));
    };

    const onClickListCreateSchedule = () => {
        const defaultStartTime = dayjs(selectedDay).hour(9).minute(0).second(0).millisecond(0);
        setSelectedSchedule({
            scheduleStartTime: defaultStartTime.format('YYYY-MM-DD HH:mm'),
        });
        createLock.run(() => setIsCreateModal(true));
    };

    const applyListRange = useCallback((startDate: string, endDate: string) => {
        if (!validateScheduleListRange(startDate, endDate).isValid) return;
        setListRangeStartDate(startDate);
        setListRangeEndDate(endDate);
    }, []);

    const retryListRange = () => {
        listRangeScheduleQuery.refetch();
        refetchListRangeGroupSessions();
        if (isNewOpportunityEnabled) refetchListRangeNewOpportunity();
    };

    const resetListRangeToCurrentView = useCallback(() => {
        const nextRange = getDefaultListRange(pageType, selectedDay);
        applyListRange(nextRange.startDate, nextRange.endDate);
    }, [applyListRange, pageType, selectedDay]);

    // 날짜 클릭시 달력나오게
    const onClickCalendar = () => {
        setIsCalendar(true);
    };

    // 수업일정 클릭이벤트
    const onClickSchedule = (item: any) => {
        // 그룹/1:2/1:3 세션이면 그룹 상세 팝오버
        if (item.classType && item.classType !== 'PRIVATE_1ON1') {
            setGroupSessionDetailId(item._id);
            return;
        }
        setDetailObject({
            ...item,
            memberId: resolveHistoryMemberId(item),
        });
        setIsScheduleDetailModal(true);
    };

    // 수업신청 클릭이벤트
    const onClickRequest = (item: any) => {
        setDetailObject(item);
        setIsRequestDetailModal(true);
    };

    // OFF, 일반일정 클릭이벤트
    const onClickEtcOffSchedule = (item: any) => {
        setDetailObject(item);
        setIsOffEtcDetailModal(true);
    };

    const onClickNewOpportunity = (item: any) => {
        setDetailObject(item);
        setIsNewOpportunityDetailModal(true);
    };

    const onClickSubDate = () => {
        if (pageType === 'daily') {
            setSelectedDay(() => dayjs(selectedDay).subtract(1, 'day'));
        } else {
            setSelectedDay(() => dayjs(selectedDay).subtract(1, 'week'));
        }
    };

    const onClickAddDate = () => {
        if (pageType === 'daily') {
            setSelectedDay(() => dayjs(selectedDay).add(1, 'day'));
        } else {
            setSelectedDay(() => dayjs(selectedDay).add(1, 'week'));
        }
    };

    const onClickDateToToday = () => {
        setSelectedDay(dayjs());
    };

    const queryClient = useQueryClient();

    const {
        data: coachNameList = [],
        refetch: refreshCoachNameList,
    } = useQuery(
        ['getCoachListForCenterQuery', authObject?.centerId, false],
        () => getCoachNameListAPI(authObject.centerId, authorization, false),
        {
            enabled: !!authObject?.centerId && !!authorization,
            keepPreviousData: true,
            select: selectCoachNameList,
            staleTime: 60 * 1000,
            suspense: true,
        }
    );

    const smartRange = useMemo(
        () => getSmartScheduleRange(smartPeriod, selectedDay),
        [smartPeriod, selectedDay]
    );
    const isSmartRangeQueryEnabled =
        viewMode === 'smart' &&
        !!authorization &&
        !!authObject?.centerId &&
        !authObject?.testAccount;
    const isSmartGroupSessionsQueryEnabled = viewMode === 'smart' && !!authObject?.centerId;
    const isSmartNewOpportunityQueryEnabled =
        viewMode === 'smart' &&
        isNewOpportunityEnabled &&
        !!authorization &&
        !!authObject?.centerId &&
        !authObject?.testAccount;

    const smartRangeScheduleQuery = useQuery(
        ['getSmartRangeSchedulesAPI', authObject?.centerId, smartRange.startDate, smartRange.endDate],
        async () => {
            const res = await getRangeScheduleAPI(
                authObject.centerId,
                smartRange.startDate,
                smartRange.endDate,
                '',
                authorization
            );
            if (res?.status !== 200) {
                throw new Error('일정을 불러오지 못했습니다.');
            }
            return Array.isArray(res?.data?.schedules) ? res.data.schedules : [];
        },
        {
            enabled: isSmartRangeQueryEnabled,
            keepPreviousData: true,
            staleTime: 60 * 1000,
        }
    );

    const smartGroupSessionsQuery = useGroupSessions({
        centerId: authObject?.centerId,
        startDate: smartRange.startDate,
        endDate: smartRange.endDate,
        enabled: isSmartGroupSessionsQueryEnabled,
    });

    const smartNewOpportunityQuery = useQuery(
        [
            'newOpportunityCalendar',
            'smart',
            authObject?.centerId,
            smartRange.startDate,
            smartRange.endDate,
        ],
        async () =>
            await getNewOpportunityCalendarAPI(authObject.centerId, authorization, {
                startTime: smartRange.startTime,
                endTime: smartRange.endTime,
            }),
        {
            enabled: isSmartNewOpportunityQueryEnabled,
            staleTime: 60 * 1000,
        }
    );

    const isSmartCoachOrderReady =
        isSmartCoachOrderQueryEnabled &&
        getDailySchedule.data?.status === 200 &&
        Array.isArray(getDailySchedule.data?.data?.dailySchedules);
    const smartOrderedCoachRoster = useMemo(() => {
        if (!isSmartCoachOrderReady) return [];

        const coachMetaById = new Map<string, any>(
            (coachNameList || []).map((coach: any) => [
                String(coach.object?.coachId || coach.code),
                coach,
            ] as [string, any])
        );
        const seenCoachIds = new Set<string>();
        const dailySchedules: any[] = getDailySchedule.data.data.dailySchedules || [];

        return dailySchedules.reduce<any[]>((orderedCoaches: any[], coach: any, index: number) => {
            const coachId = String(coach?.coachId || '').trim();
            if (!coachId || seenCoachIds.has(coachId)) return orderedCoaches;
            seenCoachIds.add(coachId);

            const metadata = coachMetaById.get(coachId);
            orderedCoaches.push({
                ...(metadata?.object || {}),
                ...coach,
                coachId,
                coachName: coach.coachName || metadata?.value || metadata?.object?.name || '이름 없음',
                coachProfileImageUrl:
                    coach.coachProfileImageUrl ||
                    metadata?.object?.coachProfileImageUrl ||
                    metadata?.object?.profileImageUrl,
                order: index,
            });
            return orderedCoaches;
        }, []);
    }, [coachNameList, getDailySchedule.data, isSmartCoachOrderReady]);
    const smartCoachSource = isSmartCoachOrderReady ? smartOrderedCoachRoster : coachNameList;

    const smartScheduleData = useMemo(
        () =>
            normalizeSmartScheduleData({
                rangeSchedules: (viewMode === 'list' ? listRangeScheduleQuery.data : smartRangeScheduleQuery.data) || [],
                groupSessions: (viewMode === 'list' ? listRangeGroupSessions : smartGroupSessionsQuery.data) || [],
                newOpportunities: smartNewOpportunityQuery.data?.data?.results || [],
                coaches: smartCoachSource || [],
            }),
        [
            smartGroupSessionsQuery.data,
            smartNewOpportunityQuery.data,
            smartRangeScheduleQuery.data,
            smartCoachSource,
            viewMode,
            listRangeScheduleQuery.data,
            listRangeGroupSessions,
        ]
    );
    const smartCoachOrderData = smartOrderedCoachRoster;

    const hasCurrentSmartRangeData =
        smartRangeScheduleQuery.dataUpdatedAt > 0 && !smartRangeScheduleQuery.isPreviousData;
    const smartScheduleError =
        isSmartRangeQueryEnabled && smartRangeScheduleQuery.error && !hasCurrentSmartRangeData;
    const partialSmartScheduleSources = [
        isSmartRangeQueryEnabled && smartRangeScheduleQuery.error && hasCurrentSmartRangeData ? '기본 일정' : '',
        isSmartGroupSessionsQueryEnabled && smartGroupSessionsQuery.error ? '그룹 수업' : '',
        isSmartNewOpportunityQueryEnabled && smartNewOpportunityQuery.error ? '신규기회' : '',
    ].filter(Boolean);
    const smartScheduleWarning = partialSmartScheduleSources.length > 0
        ? `${partialSmartScheduleSources.join('·')} 일부를 불러오지 못했어요. 표시된 일정은 계속 확인할 수 있습니다.`
        : '';
    const isSmartScheduleLoading =
        (isSmartRangeQueryEnabled && smartRangeScheduleQuery.isLoading) ||
        (isSmartGroupSessionsQueryEnabled && smartGroupSessionsQuery.isLoading) ||
        (isSmartNewOpportunityQueryEnabled && smartNewOpportunityQuery.isLoading) ||
        (isSmartCoachOrderQueryEnabled && getDailySchedule.isLoading);
    const isSmartScheduleFetching =
        (isSmartRangeQueryEnabled && smartRangeScheduleQuery.isFetching) ||
        (isSmartGroupSessionsQueryEnabled && smartGroupSessionsQuery.isFetching) ||
        (isSmartNewOpportunityQueryEnabled && smartNewOpportunityQuery.isFetching) ||
        (isSmartCoachOrderQueryEnabled && getDailySchedule.isFetching);

    const retrySmartSchedule = () => {
        if (isSmartRangeQueryEnabled && smartRangeScheduleQuery.isError) smartRangeScheduleQuery.refetch();
        if (isSmartGroupSessionsQueryEnabled && smartGroupSessionsQuery.isError) smartGroupSessionsQuery.refetch();
        if (isSmartNewOpportunityQueryEnabled && smartNewOpportunityQuery.isError) smartNewOpportunityQuery.refetch();
        refreshCoachNameList();
    };

    const onOpenSmartEvent = (event: SmartScheduleEvent) => {
        const groupSessionId = resolveSmartScheduleGroupSessionId(event);
        if (groupSessionId) {
            setGroupSessionDetailId(groupSessionId);
            return;
        }

        const item = event.rawItems[0];
        if (!item) return;
        if (event.kind === 'request') {
            onClickRequest(item);
        } else if (event.kind === 'general' || event.kind === 'off') {
            onClickEtcOffSchedule(item);
        } else if (event.kind === 'opportunity') {
            onClickNewOpportunity(item);
        } else {
            onClickSchedule(item);
        }
    };

    const onCreateSmartSlot = ({
        date,
        startTime,
        coachId,
        coachName,
    }: {
        date: string;
        startTime: string;
        coachId?: string;
        coachName?: string;
    }) => {
        const scheduleStartTime = toSmartScheduleKst(`${date} ${startTime}`);
        setPageType('daily');
        setSelectedDay(scheduleStartTime);
        setSelectedSchedule({
            scheduleStartTime: scheduleStartTime.format('YYYY-MM-DD HH:mm'),
            coachId: coachId || '',
            coachName: coachName || '',
        });
        createLock.run(() => setIsCreateModal(true));
    };

    // 스케줄 신청 수락 API : (patch)
    const approveScheduleRequest = useMutation({
        mutationFn: async (scheduleObject: any) =>
            await approveScheduleRequestAPI(authObject?.centerId, scheduleObject._id, authorization),
        onSuccess: (res) => {
            if (res?.status === 200) {
                queryClient.invalidateQueries({ queryKey: ['getDailyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getWeeklyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getSmartRangeSchedulesAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getListRangeSchedulesAPI'] });
                setIsRequestDetailModal(false);
                createAlert('등록 완료!', '대기중인 수업이 성공적으로 등록되었어요!', false);
                return;
            }

            // 공유 수강권 실패(회차 소진·풀 변경·동시 처리)는 다시 눌러도 같은 결과다.
            // 원인을 먼저 알려 재시도 안내로 떨어지지 않게 한다.
            const sharedMessage = getSharedScheduleErrorMessage(res);
            if (sharedMessage) {
                createAlert('수업 신청 수락 실패', sharedMessage, true);
                return;
            }

            if (res?.status === 400) {
                createAlert('오류', '해당 시간에는 스케줄을 생성 할 수 없습니다', true);
                return;
            }

            if (res?.status === 410 || res?.status === 420 || res?.status === 430) {
                createAlert('중복', '해당 시간에 이미 스케줄이 등록되어있습니다!\n다시 한번 확인해주세요!', true);
                return;
            }

            createAlert(
                '수업 신청 수락 실패',
                res?.data?.message || '요청을 처리하지 못했어요.\n잠시 후 다시 시도해주세요.',
                true,
            );
        },
        onError: () => {
            createAlert('오류', '오류가 발생하였습니다!\n다시 한번 등록해주세요!', true);
        },
    });

    // 스케줄 신청 거절 API : (patch)
    const rejectScheduleRequest = useMutation({
        mutationFn: async (scheduleObject: any) =>
            await rejectScheduleRequestAPI(authObject?.centerId, scheduleObject._id, authorization),
        onSuccess: (res) => {
            if (res?.status === 200) {
                queryClient.invalidateQueries({ queryKey: ['getDailyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getWeeklyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getSmartRangeSchedulesAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getListRangeSchedulesAPI'] });
                return;
            }

            const sharedMessage = getSharedScheduleErrorMessage(res);
            if (sharedMessage) {
                createAlert('수업 신청 거절 실패', sharedMessage, true);
                return;
            }

            if (res?.status === 400) {
                createAlert('오류', '해당 시간에는 스케줄을 생성 할 수 없습니다', true);
                return;
            }

            if (res?.status === 410 || res?.status === 420 || res?.status === 430) {
                createAlert('중복', '해당 시간에 이미 스케줄이 등록되어있습니다!\n다시 한번 확인해주세요!', true);
                return;
            }

            createAlert(
                '수업 신청 거절 실패',
                res?.data?.message || '요청을 처리하지 못했어요.\n잠시 후 다시 시도해주세요.',
                true,
            );
        },
        onError: () => {
            createAlert('', '오류가 발생하였습니다!\n다시 한번 등록해주세요!', true);
        },
    });

    // 수업일정 신청 수락하기 버튼 클릭이벤트
    const onClickApproveScheduleRequest = (scheduleObject: any) => {
        editLock.run(() => approveScheduleRequest.mutate(scheduleObject));
    };

    // 수업일정 신청 거절하기 버튼 클릭이벤트
    const onClickRejectScheduleRequest = (scheduleObject: any) => {
        editLock.run(() =>
            createAlert(
                '수업일정 거절을 진행하시겠습니까?',
                '대기중인 수업을 거절하시면 회원님께 알림이 전달됩니다!',
                true,
                '확인',
                () => {
                    // 확인창을 열어 둔 사이 인증이 만료됐을 수 있다 — 보내기 직전에 다시 본다
                    editLock.run(() => {
                        rejectScheduleRequest.mutate(scheduleObject);
                        setIsRequestDetailModal(false);
                    });
                },
                true,
                '취소',
                () => {}
            )
        );
    };

    const grid = useMemo(
        () =>
            Array.from({ length: 24 }, (_, rowIndex) =>
                Array.from({ length: 9 }, (_, colIndex) => ({
                    id: `${rowIndex}-${colIndex}`,
                    content:
                        colIndex === 0
                            ? rowIndex === 0
                                ? `자정 12시`
                                : rowIndex < 12
                                ? `오전 ${rowIndex.toString().padStart(2, '0')}시`
                                : rowIndex === 12
                                ? `정오 ${rowIndex}시`
                                : `오후 ${(rowIndex - 12).toString().padStart(2, '0')}시`
                            : `${rowIndex}-${colIndex}`,
                    coachName: null,
                    coachId: null,
                    coachProfileImageUrl: null,
                }))
            ),
        []
    );

    // 페이징된 데이터 기반 그리드 데이터 정제
    useEffect(() => {
        if (dailyCurrentData) {
            let grid = Array.from({ length: 24 }, (_, rowIndex) => {
                return Array.from({ length: 9 }, (_, colIndex) => ({
                    id: `${rowIndex}-${colIndex}`,
                    content:
                        colIndex === 0
                            ? rowIndex === 0
                                ? `자정 12시`
                                : rowIndex < 12
                                ? `오전 ${rowIndex.toString().padStart(2, '0')}시`
                                : rowIndex === 12
                                ? `정오 ${rowIndex}시`
                                : `오후 ${(rowIndex - 12).toString().padStart(2, '0')}시`
                            : `${rowIndex}-${colIndex}`,
                    coachName: null,
                    coachId: null,
                    coachProfileImageUrl: null,
                }));
            });

            for (let i = 0; i < dailyCurrentData.length; i++) {
                grid[0][i + 1].coachName = dailyCurrentData[i].coachName;
                grid[0][i + 1].coachId = dailyCurrentData[i].coachId;
                grid[0][i + 1].coachProfileImageUrl = dailyCurrentData[i].coachProfileImageUrl;
            }

            setGridData(grid);
        }
    }, [dailyCurrentData]);

    // // 데모버전으로 진입시
    // useEffect(() => {
    //     if (authObject?.testAccount) {
    //         setDailyScheduleList(dailySchedulesDummyData);
    //         setDailyScheduleCount(dailySchedulesCountDummyData);
    //     }
    // }, [authObject?.testAccount]);

    // 강사 8명씩 자르기 (그룹 세션 병합 데이터 사용)
    useEffect(() => {
        if (dailyDataSource) {
            setTotalCoachData(dailyDataSource);
            let filterData = dailyDataSource.slice(startIndex, endIndex);
            setDailyCurrentData(filterData);
        }
    }, [dailyDataSource, currentPage, startIndex, endIndex]);

    useEffect(() => {
        const validPage = clampLegacyDailyCoachPage(currentPage, endPage);
        if (validPage !== currentPage) setCurrentPage(validPage);
    }, [currentPage, endPage]);

    useEffect(() => {
        if (!calendarCoachId || !dailyDataSource) return;
        const focusSignature = [
            location.key || '',
            authObject?.centerId || '',
            String(calendarCoachId),
        ].join(':');
        if (focusedCoachPageSignatureRef.current === focusSignature) return;

        const coachIndex = dailyDataSource.findIndex(
            (coach: any) => String(coach.coachId) === String(calendarCoachId)
        );
        focusedCoachPageSignatureRef.current = focusSignature;
        if (coachIndex < 0) return;
        const nextPage = Math.floor(coachIndex / pageSize) + 1;
        setCurrentPage(nextPage);
    }, [authObject?.centerId, calendarCoachId, dailyDataSource, location.key, pageSize]);

    // 로딩 플래그 — 주간과 동일하게 cleanup으로 전역 오버레이 잔류를 방지
    useEffect(() => {
        if (!isLegacyDailyQueryEnabled) return;
        setIsLoading(getDailySchedule.isInitialLoading);
        return () => setIsLoading(false);
    }, [getDailySchedule.isInitialLoading, isLegacyDailyQueryEnabled, setIsLoading]);

    // 스크롤 할시 현재 스크롤위치저장
    useEffect(() => {
        if (viewMode !== 'calendar') return;
        let frameId: number | null = null;
        const handleScroll = () => {
            if (frameId !== null) return;
            frameId = window.requestAnimationFrame(() => {
                const measuredScrollY = getScheduleScrollTop();
                const nextScrollY = measuredScrollY >= 24 ? measuredScrollY : 0;
                setScrollY((current) => current === nextScrollY ? current : nextScrollY);
                frameId = null;
            });
        };

        const scrollContainer = getScheduleScrollContainer();
        scrollContainer?.addEventListener('scroll', handleScroll);
        window.addEventListener('scroll', handleScroll);
        return () => {
            if (frameId !== null) window.cancelAnimationFrame(frameId);
            scrollContainer?.removeEventListener('scroll', handleScroll);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [viewMode]);

    // 시간이 변경되었을 때 스크롤
    useEffect(() => {
        if (!!calendarType && !!calendarTimeInfo) {
            const startHour = dayjs(calendarTimeInfo).hour();
            const calendarTimeScrollY = getCalendarScrollYForHour(calendarType, startHour, cellHeight);
            const calendarTimeSelectedDay = dayjs(calendarTimeInfo);

            setPageType(calendarType);
            setSelectedDay(calendarTimeSelectedDay);
            // cleanup 없이 두면 500ms 안에 다른 페이지로 이동했을 때 그 페이지를 스크롤해버림
            const timer = window.setTimeout(() => {
                scrollSchedulePageTo(calendarTimeScrollY);
            }, 500);
            return () => window.clearTimeout(timer);
        }
    }, [calendarTimeInfo]);

    useEffect(() => {
        if (!!calendarType && !!calendarSelectedDay && hasCalendarScrollY) {
            setPageType(calendarType);
            setSelectedDay(dayjs(calendarSelectedDay));
            if (shouldRestoreCalendarScroll) {
                // cleanup 없이 두면 500ms 안에 다른 페이지로 이동했을 때 그 페이지를 스크롤해버림
                const timer = window.setTimeout(() => {
                    scrollSchedulePageTo(Number(calendarScrollY));
                }, 500);
                return () => window.clearTimeout(timer);
            }
        }
    }, [calendarType, calendarSelectedDay, calendarScrollY, hasCalendarScrollY, shouldRestoreCalendarScroll]);

    useEffect(() => {
        if (viewMode !== 'calendar') return;
        if (calendarTimeInfo || shouldRestoreCalendarScroll) return;
        if (calendarType && pageType !== calendarType) return;
        if (calendarSelectedDay && !dayjs(selectedDay).isSame(dayjs(calendarSelectedDay), 'day')) return;

        const scrollContainer = getScheduleScrollContainer();
        const autoScrollSignature = `${pageType}:${dayjs(selectedDay).format('YYYY-MM-DD')}:${cellHeight}`;
        if (lastAutoScrollSignatureRef.current === autoScrollSignature) return;

        let isCancelled = false;
        let timer: number;

        const removeAutoScrollListeners = () => {
            scrollContainer?.removeEventListener('wheel', cancelAutoScroll);
            scrollContainer?.removeEventListener('touchstart', cancelAutoScroll);
            window.removeEventListener('keydown', cancelAutoScroll);
        };

        const cancelAutoScroll = () => {
            isCancelled = true;
            lastAutoScrollSignatureRef.current = autoScrollSignature;
            window.clearTimeout(timer);
            removeAutoScrollListeners();
        };

        scrollContainer?.addEventListener('wheel', cancelAutoScroll, { passive: true });
        scrollContainer?.addEventListener('touchstart', cancelAutoScroll, { passive: true });
        window.addEventListener('keydown', cancelAutoScroll);

        timer = window.setTimeout(() => {
            if (isCancelled) return;
            lastAutoScrollSignatureRef.current = autoScrollSignature;
            scrollSchedulePageTo(getDefaultScheduleScrollY(pageType, selectedDay, cellHeight));
            removeAutoScrollListeners();
        }, 650);

        return () => {
            removeAutoScrollListeners();
            window.clearTimeout(timer);
        };
    }, [
        calendarSelectedDay,
        calendarTimeInfo,
        calendarType,
        cellHeight,
        pageType,
        selectedDay,
        shouldRestoreCalendarScroll,
        viewMode,
    ]);

    useEffect(() => {
        if (viewMode !== 'calendar') {
            lastAutoScrollSignatureRef.current = null;
            scrollSchedulePageTo(0);
        }
    }, [viewMode]);

    useEffect(() => {
        setHydratedScheduleSettingsCenterKey(scheduleSettingsCenterKey);
        setViewMode(readScheduleViewMode(scheduleViewScope));
        setPageType(readLegacyCalendarType(scheduleViewScope));
        setSmartPeriod(readSmartSchedulePeriod(scheduleViewScope));
    }, [scheduleSettingsCenterKey, scheduleViewScope]);

    useEffect(() => {
        if (hydratedScheduleSettingsCenterKey !== scheduleSettingsCenterKey) return;
        writeScheduleViewMode(scheduleViewScope, viewMode);
    }, [hydratedScheduleSettingsCenterKey, scheduleSettingsCenterKey, scheduleViewScope, viewMode]);

    useEffect(() => {
        if (hydratedScheduleSettingsCenterKey !== scheduleSettingsCenterKey) return;
        writeLegacyCalendarType(scheduleViewScope, pageType);
    }, [hydratedScheduleSettingsCenterKey, pageType, scheduleSettingsCenterKey, scheduleViewScope]);

    useEffect(() => {
        if (hydratedScheduleSettingsCenterKey !== scheduleSettingsCenterKey) return;
        writeSmartSchedulePeriod(scheduleViewScope, smartPeriod);
    }, [
        hydratedScheduleSettingsCenterKey,
        scheduleSettingsCenterKey,
        scheduleViewScope,
        smartPeriod,
    ]);

    useEffect(() => {
        if (viewMode === 'list' && !hasInitializedListRangeRef.current) {
            hasInitializedListRangeRef.current = true;
            resetListRangeToCurrentView();
        }
    }, [resetListRangeToCurrentView, viewMode]);

    useEffect(() => {
        if (viewMode === 'calendar' && pageType && selectedDay) {
            queryClient.invalidateQueries({ queryKey: ['getDailyScheduleAPI'] });
            queryClient.invalidateQueries({ queryKey: ['getWeeklyScheduleAPI'] });
        }
    }, [pageType, queryClient, selectedDay, viewMode]);

    // 호버시
    const handleMouseOver = (e: any) => {
        e.currentTarget.src = I_FLOATING_HOVER;
    };

    // 디폴트 컬러
    const handleMouseOut = (e: any) => {
        e.currentTarget.src = I_FLOATING;
    };

    // 액티브
    const handleMouseDown = (e: any) => {
        e.currentTarget.src = I_FLOATING_ONCLICK;
    };

    // 디폴트 컬러
    const handleMouseUp = (e: any) => {
        e.currentTarget.src = I_FLOATING;
    };
    // 강사이름 선택시
    const handleSelect = (selectedValue: any) => {
        setselectedCoach(selectedValue);
    };

    // 순서 변경 모달 열기
    const handleOrderChange = () => {
        setIsOrderChangeModal(true);
    };

    // 순서 변경 API : (patch)
    const updateCoachOrder = useMutation({
        mutationFn: async (coachIds: string[]) => await patchCoachOrder(authObject?.centerId, coachIds, authorization),
        onSuccess: (res, coachIds) => {
            if (res?.status === 200) {
                const dailyScheduleQueryKey = [
                    'getDailyScheduleAPI',
                    authObject?.centerId,
                    dayjs(selectedDay).format('YYYY-MM-DD'),
                ];
                queryClient.setQueryData(dailyScheduleQueryKey, (cachedResponse: any) => {
                    if (!Array.isArray(cachedResponse?.data?.dailySchedules)) return cachedResponse;
                    return {
                        ...cachedResponse,
                        data: {
                            ...cachedResponse.data,
                            dailySchedules: sortCoachRowsByIds(cachedResponse.data.dailySchedules, coachIds),
                        },
                    };
                });
                setDailyScheduleList((current: any[]) =>
                    Array.isArray(current) ? sortCoachRowsByIds(current, coachIds) : current
                );
                queryClient.invalidateQueries({ queryKey: ['getDailyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getWeeklyScheduleAPI'] });
                queryClient.invalidateQueries({ queryKey: ['fetchCoachNameList'] });
                queryClient.invalidateQueries({ queryKey: ['getCoachListForCenterQuery', authObject?.centerId] });
                setIsOrderChangeModal(false);
                return;
            }

            if (res?.status === 400) {
                createAlert('오류', '강사 목록이 변경되어 순서를 저장하지 못했습니다. 새로고침 후 다시 시도해주세요.', true);
            } else if (res?.status === 401) {
                createAlert('오류', '인증이 필요합니다.', true);
            } else if (res?.status === 403) {
                createAlert('오류', '권한이 없습니다.', true);
            } else if (res?.status === 404) {
                createAlert('오류', '센터를 찾을 수 없습니다.', true);
            } else {
                createAlert('오류', '순서 변경 중 오류가 발생했습니다.', true);
            }
        },
        onError: (error: any) => {
            const status = error?.response?.status || error?.status;
            if (status === 400) {
                createAlert('오류', '순서 변경 중 오류가 발생했습니다. 새로고침 후 다시 시도해주세요', true);
            } else if (status === 401) {
                createAlert('오류', '인증이 필요합니다.', true);
            } else if (status === 403) {
                createAlert('오류', '권한이 없습니다.', true);
            } else if (status === 404) {
                createAlert('오류', '센터를 찾을 수 없습니다.', true);
            } else {
                createAlert('오류', '순서 변경 중 오류가 발생했습니다.', true);
            }
        },
    });

    // 순서 변경 적용
    const handleOrderApply = (reorderedCoaches: any[]) => {
        const coachIds = reorderedCoaches
            .map((coach) => String(coach.coachId || '').trim())
            .filter(Boolean);
        if (coachIds.length === 0) {
            createAlert('오류', '순서를 변경할 강사 목록을 불러오지 못했습니다.', true);
            return;
        }
        updateCoachOrder.mutate(coachIds);
    };

    const activeCalendarType: 'daily' | 'weekly' =
        viewMode === 'smart' ? (smartPeriod === 'week' ? 'weekly' : 'daily') : pageType;
    const quickMoveGroupSourceFailed = viewMode === 'calendar'
        ? (pageType === 'daily' ? isLegacyDailyGroupError : isLegacyWeeklyGroupError)
        : viewMode === 'list' ? isListRangeGroupSessionsError : !!smartGroupSessionsQuery.error;
    const quickMoveSlotEvents = viewMode === 'calendar'
        ? (pageType === 'daily' ? legacyDailyScheduleEvents : legacyWeeklyScheduleEvents)
        : smartScheduleData.events;

    return (
        <QuickMoveProvider centerId={authObject?.centerId || ''} authorization={authorization || ''}
            slotEvents={quickMoveSlotEvents} groupSourceFailed={quickMoveGroupSourceFailed} enabled={viewMode !== 'calendar'}>
        <Col style={{ width: '100%' }}>
        <Row style={{ width: '100%', paddingTop: 76 }}>
            <ColContainer style={{ width: '100%' }}>
                {viewMode !== 'calendar' && (
                    <ScheduleViewDock aria-label="스케줄 보기 전환 영역">
                        <ScheduleViewSwitcher viewMode={viewMode} onChange={handleViewModeChange} />
                    </ScheduleViewDock>
                )}

                {viewMode === 'calendar' && (
                    <ScheduleTop
                        pageType={pageType}
                        setPageType={setPageType}
                        scheduleCount={
                            pageType === 'daily' ? legacyDailyScheduleCount : legacyWeeklyScheduleCount
                        }
                        scheduleCountBreakdown={
                            pageType === 'daily'
                                ? legacyDailyScheduleCountBreakdown
                                : legacyWeeklyScheduleCountBreakdown
                        }
                        scheduleCountByCoach={legacyWeeklyScheduleCountByCoach}
                        selectedCoachId={selectedCoach?.code}
                        viewMode={viewMode}
                        setViewMode={handleViewModeChange}
                        selectedDay={selectedDay}
                        onClickSubDate={onClickSubDate}
                        onClickAddDate={onClickAddDate}
                        onClickDateToToday={onClickDateToToday}
                        onClickCalendar={onClickCalendar}
                        coachNameList={coachNameList}
                        handleSelect={handleSelect}
                        cellHeight={cellHeight}
                        onZoomIn={handleZoomIn}
                        onZoomOut={handleZoomOut}
                    />
                )}

                {viewMode === 'smart' && (
                    <SmartScheduleView
                        centerId={authObject?.centerId}
                        period={smartPeriod}
                        selectedDay={selectedDay}
                        coaches={smartScheduleData.coaches}
                        events={smartScheduleData.events}
                        isLoading={isSmartScheduleLoading}
                        isFetching={isSmartScheduleFetching}
                        errorMessage={smartScheduleError ? '스케줄을 불러오지 못했습니다.' : ''}
                        warningMessage={smartScheduleWarning}
                        onRetry={retrySmartSchedule}
                        onPeriodChange={setSmartPeriod}
                        onSelectedDayChange={setSelectedDay}
                        onOpenEvent={onOpenSmartEvent}
                        onCreateSlot={onCreateSmartSlot}
                        onOpenCoachOrder={
                            smartPeriod === 'day' && isSmartCoachOrderReady
                                ? handleOrderChange
                                : undefined
                        }
                    />
                )}

                {viewMode === 'calendar' &&
                    ((pageType === 'daily' && isDailyNewOpportunityError) ||
                        (pageType === 'weekly' &&
                            isWeeklyNewOpportunityError)) && (
                        <div
                            role="alert"
                            style={{
                                margin: '0 24px 12px',
                                minHeight: 48,
                                border: '1px solid #FCD34D',
                                borderRadius: 10,
                                background: '#FFFBEB',
                                color: '#92400E',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 16,
                                padding: '10px 14px',
                                fontSize: 13,
                                fontWeight: 750,
                            }}
                        >
                            <span>
                                일반 수업 일정은 표시 중이지만 OT·상담 일정을
                                불러오지 못했습니다.
                            </span>
                            <button
                                type="button"
                                onClick={() =>
                                    pageType === 'daily'
                                        ? refetchDailyNewOpportunity()
                                        : refetchWeeklyNewOpportunity()
                                }
                                disabled={
                                    pageType === 'daily'
                                        ? isDailyNewOpportunityFetching
                                        : isWeeklyNewOpportunityFetching
                                }
                                style={{
                                    minHeight: 34,
                                    border: '1px solid #F59E0B',
                                    borderRadius: 8,
                                    background: '#FFFFFF',
                                    color: '#92400E',
                                    padding: '0 12px',
                                    fontWeight: 850,
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {(pageType === 'daily'
                                    ? isDailyNewOpportunityFetching
                                    : isWeeklyNewOpportunityFetching)
                                    ? '다시 불러오는 중'
                                    : 'OT·상담 다시 시도'}
                            </button>
                        </div>
                    )}

                {viewMode === 'calendar' && pageType === 'daily' && (
                    <DailySchedule
                        setIsLoading={setIsLoading}
                        grid={grid}
                        currentDate={currentDate}
                        authorization={authorization}
                        authObject={authObject}
                        createAlert={createAlert}
                        createTwoButtonAlert={createTwoButtonAlert}
                        selectedDay={selectedDay}
                        setSelectedDay={setSelectedDay}
                        selectedSchedule={selectedSchedule}
                        setSelectedSchedule={setSelectedSchedule}
                        onClickFloatingButton={onClickFloatingButton}
                        onClickSchedule={onClickSchedule}
                        onClickRequest={onClickRequest}
                        onClickEtcOffSchedule={onClickEtcOffSchedule}
                        onClickNewOpportunity={onClickNewOpportunity}
                        onClickApproveScheduleRequest={onClickApproveScheduleRequest}
                        onClickRejectScheduleRequest={onClickRejectScheduleRequest}
                        scrollY={scrollY}
                        dailyCurrentData={dailyCurrentData}
                        scheduleCountByCoach={legacyDailyScheduleCountByCoach}
                        gridData={gridData}
                        onClickHover={onClickHover}
                        currentPage={currentPage}
                        endPage={endPage}
                        handleNextPage={handleNextPage}
                        handlePrevPage={handlePrevPage}
                        handleOrderChange={handleOrderChange}
                        cellHeight={cellHeight}
                    />
                )}

                {viewMode === 'calendar' && pageType === 'weekly' && (
                    <WeeklySchedule
                        setIsLoading={setIsLoading}
                        grid={grid}
                        currentDate={currentDate}
                        authorization={authorization}
                        authObject={authObject}
                        selectedDay={selectedDay}
                        setSelectedDay={setSelectedDay}
                        onClickFloatingButton={onClickFloatingButton}
                        onClickSchedule={onClickSchedule}
                        onClickRequest={onClickRequest}
                        onClickEtcOffSchedule={onClickEtcOffSchedule}
                        onClickNewOpportunity={onClickNewOpportunity}
                        onClickApproveScheduleRequest={onClickApproveScheduleRequest}
                        onClickRejectScheduleRequest={onClickRejectScheduleRequest}
                        scrollY={scrollY}
                        weeklyScheduleList={weeklyScheduleList}
                        weeklyGridData={mergedWeeklyGridData}
                        lengthArr={mergedLengthArr}
                    />
                )}

                {viewMode === 'list' && (
                    <ScheduleListView
                        pageType={pageType}
                        selectedDay={selectedDay}
                        dailyDataSource={dailyDataSource}
                        weeklyGridData={mergedWeeklyGridData}
                        rangeStartDate={listRangeStartDate}
                        rangeEndDate={listRangeEndDate}
                        rangeScheduleItems={listRangeScheduleItems}
                        centerId={authObject?.centerId}
                        isRangeLoading={isListRangeLoading}
                        rangeErrorMessage={listRangeErrorMessage}
                        onApplyRange={applyListRange}
                        onRetryRange={retryListRange}
                        onResetRangeToCurrentView={resetListRangeToCurrentView}
                        onClickSchedule={onClickSchedule}
                        onClickRequest={onClickRequest}
                        onClickEtcOffSchedule={onClickEtcOffSchedule}
                        onClickNewOpportunity={onClickNewOpportunity}
                        onClickApproveScheduleRequest={onClickApproveScheduleRequest}
                        onClickRejectScheduleRequest={onClickRejectScheduleRequest}
                        onClickCreateSchedule={onClickListCreateSchedule}
                        createTwoButtonAlert={createTwoButtonAlert}
                    />
                )}

                {/* 플로팅 버튼 클릭시 새로운 일정 생성 모달 */}
                {isCreateModal && (
                    <ModalCreateClass
                        modalVisible={isCreateModal}
                        setModalVisible={setIsCreateModal}
                        navigate={navigate}
                        createAlert={createAlert}
                        selectedSchedule={selectedSchedule}
                        setSelectedSchedule={setSelectedSchedule}
                        calendarType={activeCalendarType}
                        calendarSelectedDay={dayjs(selectedDay).format('YYYY-MM-DD')}
                        calendarScrollY={scrollY}
                        onClickGroupSession={() => {
                            setIsCreateModal(false);
                            // 빈 셀 클릭으로 selectedSchedule에 날짜/시간/강사가 있으면 전달
                            const ss = selectedSchedule;
                            if (ss?.scheduleStartTime) {
                                const d = dayjs(ss.scheduleStartTime);
                                setQuickGroupDefaults({
                                    date: d.format('YYYY-MM-DD'),
                                    startTime: d.format('HH:mm'),
                                    endTime: d.add(50, 'minute').format('HH:mm'),
                                    coachId: ss.coachId || '',
                                    coachName: ss.coachName || '',
                                });
                            } else {
                                setQuickGroupDefaults({});
                            }
                            setIsQuickGroupModal(true);
                        }}
                    />
                )}

                {/* 빠른 그룹 세션 생성 (템플릿 선택) */}
                {isQuickGroupModal && (
                    <QuickGroupSessionModal
                        onClose={() => setIsQuickGroupModal(false)}
                        onOpenFullCreate={() => createLock.run(() => setIsGroupSessionCreateModal(true))}
                        createAlert={createAlert}
                        defaultDate={quickGroupDefaults.date}
                        defaultStartTime={quickGroupDefaults.startTime}
                        defaultEndTime={quickGroupDefaults.endTime}
                        defaultCoachId={quickGroupDefaults.coachId}
                        defaultCoachName={quickGroupDefaults.coachName}
                    />
                )}

                {/* 새 수업 템플릿 만들기 (전체 폼) */}
                {isGroupSessionCreateModal && (
                    <GroupSessionCreateModal
                        onClose={() => setIsGroupSessionCreateModal(false)}
                        createAlert={createAlert}
                        scheduleCreateLock
                    />
                )}

                {/* 스케줄 동작 잠금 카드(body 포털) */}
                {createLock.gate}
                {editLock.gate}

                {/* 그룹 세션 상세 팝오버 */}
                {groupSessionDetailId && (
                    <GroupSessionDetailPopover
                        sessionId={groupSessionDetailId}
                        onClose={() => setGroupSessionDetailId(null)}
                        createAlert={createAlert}
                    />
                )}

                {/* 달력 모달 pageType에 따른 분기 */}
                {isCalendar && pageType === 'daily' && (
                    <Calendar
                        selectedDay={new Date(selectedDay)}
                        setSelectedDay={setSelectedDay}
                        setIsCalendar={setIsCalendar}
                        isPrevMonth={true}
                        isNextMonth={true}
                        style={{
                            width: 420,
                            height: 370,
                            borderRadius: 24,
                            backgroundColor: colors.WHITE_50,
                            boxShadow: '8px 8px 24px 0px #00000052',

                            position: 'fixed',
                            zIndex: 1999,
                            marginTop: 70,
                            marginLeft: 230,
                        }}
                    />
                )}

                {isCalendar && pageType === 'weekly' && (
                    <WeeklyCalendar
                        selectedDay={new Date(selectedDay)}
                        setSelectedDay={setSelectedDay}
                        isPrevMonth={true}
                        isNextMonth={true}
                        setIsCalendar={setIsCalendar}
                        style={{
                            width: 420,
                            height: 370,
                            borderRadius: 24,
                            backgroundColor: colors.WHITE_50,
                            boxShadow: '8px 8px 24px 0px #00000052',

                            position: 'fixed',
                            zIndex: 1999,
                            marginTop: 70,
                            marginLeft: 230,
                        }}
                    />
                )}

                {/* 하단 플로팅 버튼 */}
                {viewMode === 'calendar' && (
                    <Row
                        onClick={onClickFloatingButton}
                        style={{
                            width: 64,
                            height: 64,
                            position: 'fixed',
                            right: 64,
                            bottom: 64,
                            cursor: 'pointer',
                        }}
                    >
                        <img
                            alt=""
                            src={I_FLOATING}
                            style={{ width: 64, height: 64 }}
                            onMouseOver={handleMouseOver}
                            onMouseOut={handleMouseOut}
                            onMouseDown={handleMouseDown}
                            onMouseUp={handleMouseUp}
                        />
                    </Row>
                )}
            </ColContainer>

            {isScheduleDetailModal && detailObject && (
                <ScheduleDetailModal
                    modalVisible={isScheduleDetailModal}
                    setModalVisible={setIsScheduleDetailModal}
                    detailObject={detailObject}
                    createAlert={createAlert}
                    createTwoButtonAlert={createTwoButtonAlert}
                    calendarType={activeCalendarType}
                    calendarSelectedDay={dayjs(selectedDay).format('YYYY-MM-DD')}
                />
            )}

            {isRequestDetailModal && detailObject && (
                <RequestDetailModal
                    modalVisible={isRequestDetailModal}
                    setModalVisible={setIsRequestDetailModal}
                    navigate={navigate}
                    detailObject={detailObject}
                    setDetailObject={setDetailObject}
                    onClickRejectScheduleRequest={onClickRejectScheduleRequest}
                    onClickApproveScheduleRequest={onClickApproveScheduleRequest}
                    createAlert={createAlert}
                    authObject={authObject}
                    calendarType={activeCalendarType}
                    calendarSelectedDay={dayjs(selectedDay).format('YYYY-MM-DD')}
                    calendarScrollY={scrollY}
                />
            )}

            {isOffEtcDetailModal && detailObject && (
                <OffEtcDetailModal
                    modalVisible={isOffEtcDetailModal}
                    setModalVisible={setIsOffEtcDetailModal}
                    navigate={navigate}
                    detailObject={detailObject}
                    setDetailObject={setDetailObject}
                    createAlert={createAlert}
                    createTwoButtonAlert={createTwoButtonAlert}
                    authObject={authObject}
                    calendarType={activeCalendarType}
                    calendarSelectedDay={dayjs(selectedDay).format('YYYY-MM-DD')}
                    calendarScrollY={scrollY}
                />
            )}

            {isNewOpportunityDetailModal && detailObject && (
                <NewOpportunityDetailModal
                    modalVisible={isNewOpportunityDetailModal}
                    setModalVisible={setIsNewOpportunityDetailModal}
                    detailObject={detailObject}
                    setDetailObject={setDetailObject}
                    createAlert={createAlert}
                />
            )}

            {/* 순서 변경 모달 */}
            {isOrderChangeModal && (
                <OrderChangeModal
                    isOpen={isOrderChangeModal}
                    onClose={() => setIsOrderChangeModal(false)}
                    coaches={viewMode === 'smart' ? smartCoachOrderData : totalCoachData || []}
                    onApply={handleOrderApply}
                    isSaving={updateCoachOrder.isLoading}
                />
            )}
        </Row>

        {/* 사업자 정보 (카드사 심사용) */}
        <div style={{ width: '100%', marginTop: 40 }}>
            <BusinessInfoFooter />
        </div>
        </Col>
        </QuickMoveProvider>
    );
};

export default SchedulePage;
